/**
 * Cart redesign — live sync for the discount tickets, discount summary
 * line and header item count.
 *
 * Hooks into the SAME signals assets/cart-upsell.js already uses (the
 * theme:cart:add / theme:cart:change / theme:cart:refresh /
 * theme:product:add custom events, plus a fetch() wrapper that detects
 * any /cart/add|change|update|clear request) so this file never talks to
 * the cart on its own schedule — it only reacts to cart mutations the
 * theme already knows about, then re-reads the live cart from /cart.js
 * (the same AJAX Cart API already used elsewhere in the theme).
 *
 * The ticket "active" state and the discount summary amount are driven
 * exclusively by discounts Shopify actually applied to the cart — read
 * from both cart.cart_level_discount_applications and
 * cart.items[].line_level_discount_allocations (this store's automatic
 * quantity discount is allocated per line item, not at the cart level),
 * never by a client-side recomputation of the percentage — see
 * getAppliedDiscount()/updateTickets()/updateDiscountLine() below.
 */
(function () {
  'use strict';

  function formatMoney(cents) {
    try {
      if (window.theme && typeof window.theme.formatMoney === 'function') {
        var format = window.theme.moneyWithCurrencyFormat || window.theme.moneyFormat;
        return window.theme.formatMoney(cents, format);
      }
    } catch (e) {
      /* fall through to basic fallback */
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function parsePercentFromTitle(title) {
    var match = /(\d+)\s*%/.exec(title || '');
    return match ? parseInt(match[1], 10) : null;
  }

  // Known real automatic-discount schedule, confirmed against the store's
  // configured discounts (Buy 2/15%, Buy 3/20%, Buy 4/25%, Buy 5/25%).
  // Used only as a fallback label for percentages when the discount title
  // can't be parsed — the active/inactive state itself is always gated on
  // Shopify actually having applied a discount (see updateTickets below).
  var FALLBACK_PERCENT_BY_COUNT = { 2: 15, 3: 20, 4: 25, 5: 25 };

  function updateHeaderCount(cart) {
    // Note: the theme already renders the surrounding "( )" via CSS
    // (.cart__items-count::before/::after) so this only ever sets the
    // bare number — adding literal parentheses here would double them up.
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.setAttribute('data-cart-count', cart.item_count);
      el.textContent = cart.item_count;
    });
  }

  // This store's automatic quantity discount (Buy 2/3/4/5+) is applied per
  // line item, not at the cart/order level, so it shows up in
  // cart.items[].line_level_discount_allocations rather than
  // cart.cart_level_discount_applications. Read both so the tickets and
  // discount summary line always reflect whatever Shopify actually applied.
  function getAppliedDiscount(cart) {
    var applications = cart.cart_level_discount_applications || [];
    if (applications.length > 0) {
      var totalAmount = 0;
      applications.forEach(function (app) { totalAmount += app.total_allocated_amount; });
      return { title: applications[0].title, amount: totalAmount };
    }

    var title = null;
    var amount = 0;
    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (alloc) {
        if (!title) title = alloc.discount_application && alloc.discount_application.title;
        amount += alloc.amount;
      });
    });
    return title ? { title: title, amount: amount } : null;
  }

  function updateTickets(cart) {
    var wraps = document.querySelectorAll('[data-discount-tickets]');
    if (!wraps.length) return;

    var count = cart.item_count;
    var applied = getAppliedDiscount(cart);
    var hasRealDiscount = !!applied;
    var appliedPercent = hasRealDiscount ? parsePercentFromTitle(applied.title) : null;

    var activeTier = null; // 'bronze' | 'silver' | 'gold' | null
    if (hasRealDiscount) {
      if (count === 2) activeTier = 'bronze';
      else if (count === 3) activeTier = 'silver';
      else if (count >= 4) activeTier = 'gold';
    }

    // Gold ticket always advertises 25% (the real Buy 4 / Buy 5+ rate) —
    // only overridden if the currently applied discount title parses to a
    // different percentage than expected.
    var goldPercent = 25;
    if (activeTier === 'gold' && appliedPercent) goldPercent = appliedPercent;

    wraps.forEach(function (wrap) {
      wrap.querySelectorAll('.cartx-ticket').forEach(function (ticket) {
        var tier = ticket.getAttribute('data-tier');
        var isActive = tier === activeTier;
        ticket.classList.toggle('is-active', isActive);

        if (tier === 'gold') {
          var percentEl = ticket.querySelector('[data-ticket-percent]');
          if (percentEl) percentEl.textContent = '-' + goldPercent + '%';
        }
      });
    });
  }

  function updateDiscountLine(cart) {
    var lines = document.querySelectorAll('[data-discount-summary]');
    if (!lines.length) return;

    var applied = getAppliedDiscount(cart);

    lines.forEach(function (line) {
      if (!applied) {
        line.hidden = true;
        return;
      }
      var percent = parsePercentFromTitle(applied.title) || FALLBACK_PERCENT_BY_COUNT[cart.item_count] || '';
      var percentEl = line.querySelector('[data-discount-percent]');
      var amountEl = line.querySelector('[data-discount-amount]');
      if (percentEl) percentEl.textContent = percent + '% nuolaida';
      if (amountEl) amountEl.textContent = ('-' + formatMoney(applied.amount)).replace(/\s*[A-Z]{3}\b/, '');
      line.hidden = false;
    });
  }

  function updateShipping(cart) {
    document.querySelectorAll('[data-shipping-row]').forEach(function (row) {
      var thresholdRaw = row.getAttribute('data-free-shipping-threshold');
      var threshold = thresholdRaw ? parseFloat(thresholdRaw) * 100 : 0;
      var valueEl = row.querySelector('[data-shipping-value]');
      if (!valueEl) return;
      if (threshold > 0 && cart.total_price >= threshold) {
        valueEl.textContent = 'Nemokamai';
      } else {
        valueEl.textContent = 'Apskaičiuojama apmokant';
      }
    });
  }

  function updateTotal(cart) {
    document.querySelectorAll('[data-cart-total]').forEach(function (el) {
      el.setAttribute('data-cart-total', cart.total_price);
      el.textContent = cart.total_price === 0 ? 'Free' : formatMoney(cart.total_price);
    });
  }

  function syncAll() {
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        updateHeaderCount(cart);
        updateTickets(cart);
        updateDiscountLine(cart);
        updateShipping(cart);
        updateTotal(cart);
      })
      .catch(function (err) {
        console.error('[cart-redesign] could not sync cart', err);
      });
  }

  document.addEventListener('DOMContentLoaded', syncAll);

  [
    'theme:cart:add',
    'theme:cart:change',
    'theme:cart:refresh',
    'theme:cart:update',
    'theme:product:add'
  ].forEach(function (eventName) {
    document.addEventListener(eventName, syncAll);
  });

  // Same defensive fetch wrapper pattern already used in cart-upsell.js:
  // re-sync whenever any cart mutation endpoint resolves successfully.
  var nativeFetch = window.fetch.bind(window);
  window.fetch = function () {
    var url = arguments[0];
    var urlString = typeof url === 'string' ? url : (url && url.url) || '';
    var isCartMutation = /\/cart\/(add|change|update|clear)(\.js)?/.test(urlString);
    return nativeFetch.apply(window, arguments).then(function (response) {
      if (isCartMutation && response.ok) {
        syncAll();
      }
      return response;
    });
  };
})();
